<script setup lang="ts">
import axios from 'axios';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { qkGetApiError } from '../api-error.js';
import { createMessage } from '../legacy/message.js';
import { createModal as openModal } from '../legacy/overlay.js';

const emit = defineEmits<{ (e: 'loadinged'): void }>();

const config = qk_global.login_config ?? {
  allow_register: 1,
  allow_register_check: false,
  check_type: '',
  login_text: '用户名',
  forgot_url: '#',
  agreement: {},
};

const data = ref({
  nickname: '',
  username: '',
  password: '',
  code: '',
  invite_code: '',
  confirmPassword: '',
  loginType: '',
  captcha: '',
});

const loginType = ref(1);
const locked = ref(false);
const oauths = ref<Record<string, { type: string; icon: string; url?: string }>>({});
const login = ref<Record<string, unknown> | null>(null);
const invitePass = ref(false);
const countdown = ref(60);
const sendingCode = ref(false);
const loginForm = ref<HTMLFormElement | null>(null);

function normalizeInviteType(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function inviteEnabled(value: unknown) {
  return normalizeInviteType(value) !== 0;
}

function message(opts: string | { message: string; type?: string }) {
  createMessage(opts);
}

function createModal(name: string, opts: Record<string, unknown>) {
  openModal(name, opts);
}

function syncAutofillValues() {
  const form = loginForm.value;
  if (!form) return;

  const usernameInput = form.querySelector('input[name="username"]') as HTMLInputElement | null;
  const passwordInput = form.querySelector('input[name="password"]') as HTMLInputElement | null;

  if (usernameInput?.value.trim()) {
    data.value.username = usernameInput.value.trim();
  }
  if (passwordInput?.value) {
    data.value.password = passwordInput.value;
  }
}

function getLoginPayload() {
  syncAutofillValues();
  return {
    username: data.value.username.trim(),
    password: data.value.password,
  };
}

const https = axios;

const loginTitle = computed(() => {
  if (loginType.value === 1) return '欢迎回来';
  if (loginType.value === 2) {
    const settings = login.value as { invite_type?: number | string } | null;
    if (inviteEnabled(settings?.invite_type) && !invitePass.value) return '填写邀请码';
    return '注册新账号';
  }
  if (loginType.value === 3) return '找回密码';
  if (loginType.value === 4) return '扫码登录';
  return '';
});

const buttonText = computed(() => {
  if (loginType.value === 1) return '登录';
  if (loginType.value === 2) return '注册';
  if (loginType.value === 3) return '下一步';
  return '';
});

onMounted(() => {
  https.get(`${qk_rest_url}getLoginSettings`).then((res) => {
    oauths.value = res.data.oauths ?? {};
    login.value = res.data;
    nextTick(() => emit('loadinged'));
  }).catch((err) => {
    message({ message: qkGetApiError(err, '加载登录配置失败'), type: 'error' });
    nextTick(() => emit('loadinged'));
  });
});

function loginSubmit(e: Event) {
  e.preventDefault();
  const settings = login.value as { allow_slider_captcha?: boolean } | null;
  if (settings?.allow_slider_captcha) {
    return createModal('sliderCaptcha', {
      size: 356,
      keepAlive: false,
      props: {
        callback: (captcha: string) => {
          data.value.captcha = captcha;
          submit();
        },
      },
    });
  }
  submit();
}

function submit() {
  if (locked.value) return;
  locked.value = true;

  const settings = login.value as { invite_type?: number } | null;

  if (loginType.value === 2 && inviteEnabled(settings?.invite_type) && !invitePass.value) {
    locked.value = false;
    checkInviteCode();
    return;
  }

  if (loginType.value === 1) {
    const payload = getLoginPayload();
    if (!payload.username) {
      message({ message: '请输入账号', type: 'warning' });
      locked.value = false;
      return;
    }
    if (!payload.password) {
      message({ message: '请输入密码', type: 'warning' });
      locked.value = false;
      return;
    }

    https
      .post(`${qk_global.rest_url}jwt-auth/v1/token`, payload)
      .then((res) => {
        if (res.status === 200) {
          const token = qkGetCookie('qk_token');
          if (token) {
            https.defaults.headers.common.Authorization = `Bearer ${token}`;
          }
        }
        location.reload();
      })
      .catch((err) => {
        message({ message: qkGetApiError(err, '登录失败'), type: 'error' });
        locked.value = false;
      });
    return;
  }

  if (loginType.value === 2) {
    https
      .post(`${qk_rest_url}regeister`, data.value)
      .then(() => location.reload())
      .catch((err) => {
        message({ message: qkGetApiError(err, '注册失败'), type: 'warning' });
        locked.value = false;
      });
  }
}

function getCode() {
  syncAutofillValues();
  const username = data.value.username.trim();
  if (!username) {
    message({ message: `请输入${config.login_text}`, type: 'warning' });
    return;
  }
  if (sendingCode.value || countdown.value < 60) {
    return;
  }

  sendingCode.value = true;
  https
    .post(`${qk_rest_url}sendCode`, { username })
    .then((res) => {
      const text = res?.data?.msg || res?.data?.message || '验证码已发送';
      message({ message: text, type: 'success' });
      startCountdown();
    })
    .catch((err) => {
      message({ message: qkGetApiError(err, '验证码发送失败'), type: 'warning' });
    })
    .finally(() => {
      sendingCode.value = false;
    });
}

function checkInviteCode() {
  if (!data.value.invite_code.trim()) {
    message({ message: '请输入邀请码', type: 'error' });
    return;
  }
  if (!/^[0-9a-zA-Z]{4}(-[0-9a-zA-Z]{4}){4}$/.test(data.value.invite_code.trim())) {
    message({ message: '请输入正确格式：xxxx-xxxx-xxxx-xxxx-xxxx', type: 'error' });
    return;
  }
  https
    .post(`${qk_rest_url}checkInviteCode`, { invite_code: data.value.invite_code })
    .then(() => {
      invitePass.value = true;
      locked.value = false;
    })
    .catch((err) => {
      locked.value = false;
      message({ message: qkGetApiError(err, '邀请码无效'), type: 'error' });
    });
}

function socialLogin(type: string) {
  qkSetCookie('qk_referer_url', window.location.href);
  const item = oauths.value[type];
  if (item?.url) {
    window.location.href = item.url;
    return;
  }
  message({ message: '拉取数据中，请稍后...', type: 'success' });
  https
    .post(`${qk_rest_url}socialLogin`, { type })
    .then((res) => {
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    })
    .catch((err) => {
      message({ message: qkGetApiError(err, '社交登录失败'), type: 'error' });
    });
}

function startCountdown() {
  const intervalId = window.setInterval(() => {
    countdown.value -= 1;
    if (countdown.value <= 0) {
      window.clearInterval(intervalId);
      countdown.value = 60;
    }
  }, 1000);
}

watch(invitePass, (val) => {
  const settings = login.value as { invite_type?: number } | null;
  if (val && normalizeInviteType(settings?.invite_type) === 2) {
    data.value.invite_code = '';
  }
});

defineExpose({ destroy: () => {
  loginType.value = 1;
  locked.value = false;
  invitePass.value = false;
  data.value = {
    nickname: '',
    username: '',
    password: '',
    code: '',
    invite_code: '',
    confirmPassword: '',
    loginType: '',
    captcha: '',
  };
}});
</script>

<template>
  <div v-if="login" class="login-container">
    <div class="container-top">
      <div class="title">{{ loginTitle }}</div>
      <p
        v-if="loginType === 2 && inviteEnabled((login as any).invite_type) && !invitePass"
        style="font-size: 14px; color: var(--color-text-secondary); margin-top: 12px;"
      >
        没有邀请码？<a href="#" class="active">获取邀请码</a>
      </p>
    </div>
    <div class="container-content form-container">
      <form ref="loginForm" @submit.stop.prevent="loginSubmit">
        <div
          v-if="loginType === 2 && inviteEnabled((login as any).invite_type) && !invitePass"
          class="invite-box"
        >
          <label class="form-item">
            <input
              v-model="data.invite_code"
              type="text"
              name="invite_code"
              tabindex="1"
              spellcheck="false"
              autocomplete="off"
              placeholder="请输入邀请码"
            />
          </label>
          <div class="form-button">
            <button type="submit">提交</button>
          </div>
          <div v-if="normalizeInviteType((login as any).invite_type) === 2" class="invite-skip">
            <span @click.prevent="invitePass = true">跳过</span>
          </div>
        </div>
        <div v-else class="login-box">
          <label v-show="loginType === 2" class="form-item nickname">
            <input
              v-model="data.nickname"
              type="text"
              name="nickname"
              tabindex="1"
              spellcheck="false"
              autocomplete="off"
              placeholder="请输入昵称"
            />
          </label>
          <label class="form-item">
            <input
              v-model="data.username"
              type="text"
              name="username"
              tabindex="2"
              spellcheck="false"
              autocomplete="username"
              :placeholder="`请输入${config.login_text}`"
            />
          </label>
          <label v-show="loginType === 2 && data.username && (login as any).check_type" class="form-item">
            <input
              v-model="data.code"
              type="text"
              name="code"
              tabindex="3"
              spellcheck="false"
              autocomplete="off"
              placeholder="请输入验证码"
            />
            <div class="login-eye text" @click.prevent="countdown === 60 && !sendingCode ? getCode() : null">
              {{ sendingCode ? '发送中...' : (countdown < 60 ? `${countdown}秒后可重发` : '发送验证码') }}
            </div>
          </label>
          <label class="form-item">
            <input
              v-model="data.password"
              type="password"
              name="password"
              tabindex="4"
              autocomplete="current-password"
              spellcheck="false"
              placeholder="请输入密码"
            />
          </label>
          <div class="signin-hint">
            <div v-if="loginType === 1 && config.allow_register === 1" class="hint-text">
              还没有账号? <span @click="loginType = 2">前往注册</span>
            </div>
            <div v-if="loginType === 2" class="hint-text">
              已有帐号? <span @click="loginType = 1">立即登录</span>
            </div>
            <a
              v-if="loginType === 1"
              :href="config.forgot_url"
              target="_blank"
              class="forget-password"
            >
              忘记密码？
            </a>
          </div>
          <div class="form-button">
            <button type="submit">{{ buttonText }}</button>
          </div>
        </div>
      </form>
    </div>
    <div
      v-if="!(loginType === 2 && inviteEnabled((login as any).invite_type) && !invitePass)"
      class="social-loginbar"
    >
      <div v-if="Object.keys(oauths).length" class="separator-text">或</div>
      <div v-if="Object.keys(oauths).length" class="other-login">
        <a
          v-for="(item, index) in oauths"
          :key="item.type"
          href="javascript:void(0)"
          class="no-hover"
          :class="item.type"
          @click="socialLogin(String(index))"
        >
          <i :class="item.icon" />
        </a>
      </div>
      <div v-show="loginType === 1" class="agreement">
        登录表示您已阅读并同意
        <span v-if="config.agreement.agreement">
          <a :href="config.agreement.agreement" target="_blank">用户协议</a>
        </span>
        和
        <span v-if="config.agreement.privacy">
          <a :href="config.agreement.privacy" target="_blank">隐私政策</a>
        </span>
      </div>
    </div>
  </div>
</template>
